// src/components/common/header/Header.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useHeaderHeightVar from './useHeaderHeightVar';

// パス（header/ 配下）
import '../../../css/common/header/Header.css';
import '../../../css/common/header/HeaderAvatar.css';

import { useAuth } from '../../../contexts/AuthContext';
import { useArtistAuth } from '../../../contexts/ArtistAuthContext';
import { useMessageCount } from '../../../contexts/MessageCountContext';
import { MAINTENANCE_MODES, useMaintenance } from '../../../contexts/MaintenanceContext';
import { getLastLoginPath, setLastLoginRole } from '../../../utils/lastLoginRole';
import { getCurrentAdminProfile, getCurrentArtistProfile } from '../../../api/profiles';
import { adminLogout, artistLogout } from '../../../api/auth';
import DsButton from '../DsButton';

// 丸アイコン（画像は丸いっぱい）
import HeaderAvatar from './HeaderAvatar';
import { useHeaderAiUsage, HeaderAiAvatarRing, HeaderAiUsageMenuRows } from './HeaderAiUsage';
import logoImg from '../../../assets/branding/casticart_logo.svg';
import logoIconImg from '../../../assets/branding/casticart_icon.svg';

/* =========================
   ユーティリティ
   ========================= */

// 外側クリックで閉じる（軽量）
function useOutsideCloser(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      const el = ref.current;
      if (!el) return;
      if (!el.contains(e.target)) onClose?.();
    };
    document.addEventListener('mousedown', handler, { passive: true });
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose, ref]);
}

const computeSidebarVisibility = () => {
  if (typeof document === 'undefined') return true;
  const body = document.body;
  if (!body) return true;
  const isMobile =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 700px)').matches;
  if (isMobile) {
    return body.classList.contains('sidebar-mobile-open');
  }
  return !body.classList.contains('sidebar-collapsed');
};

const resolveAdminWorkspaceHomePath = (pathname = '') => {
  const path = String(pathname || '');
  if (path.startsWith('/admin/workspaces/task-management')) {
    return '/admin/workspaces/task-management/dashboard';
  }
  if (path.startsWith('/admin/workspaces/finance')) {
    return '/admin/workspaces/finance/dashboard';
  }
  if (path.startsWith('/admin/workspaces/concierge')) {
    return '/admin/workspaces/concierge/dashboard';
  }
  return '/admin/workspaces/event-operations/dashboard';
};

const resolveArtistWorkspaceHomePath = (pathname = '') => {
  const path = String(pathname || '');
  if (path.startsWith('/artist/workspaces/contracts')) {
    return '/artist/workspaces/contracts/dashboard';
  }
  if (path.startsWith('/artist/workspaces/concierge')) {
    return '/artist/workspaces/concierge/dashboard';
  }
  return '/artist/workspaces/event-operations/dashboard';
};

const resolveMobileHeaderPageTitle = (pathname = '') => {
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  const rules = [
    [/^\/admin\/workspaces\/event-operations\/dashboard$/, 'ダッシュボード'],
    [/^\/admin\/workspaces\/event-operations\/events\/register$/, 'イベント登録'],
    [/^\/admin\/workspaces\/event-operations\/events\/registration-complete$/, 'イベント登録完了'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/casting-registration$/, 'キャスティング設定'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/edit$/, 'イベント情報修正'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/casting-progress$/, '参加パート設定'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/ticket-sales\/analytics$/, '券売分析'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/ticket-sales$/, '券売状況登録'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/pl$/, 'イベント損益'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/timetable$/, 'タイムテーブル設計'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/timetables\/[^/]+$/, 'タイムテーブル表示'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/participant-artists$/, '参加アーティスト'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/stage-seating(?:\/preview)?$/, 'シーティング'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+$/, 'イベント詳細'],
    [/^\/admin\/workspaces\/event-operations\/events$/, 'イベント一覧'],
    [/^\/admin\/workspaces\/event-operations\/event-chat$/, 'イベントチャット'],
    [/^\/admin\/workspaces\/event-operations\/ticket-sales$/, 'チケット売上'],
    [/^\/admin\/workspaces\/event-operations\/event-options$/, 'オプション設定'],
    [/^\/admin\/workspaces\/event-operations\/genres$/, 'ジャンル管理'],
    [/^\/admin\/workspaces\/event-operations\/venues\/register$/, '会場登録'],
    [/^\/admin\/workspaces\/event-operations\/venues\/[^/]+$/, '会場詳細'],
    [/^\/admin\/workspaces\/event-operations\/venues$/, '会場一覧'],
    [/^\/admin\/workspaces\/task-management\/dashboard$/, 'タスク'],
    [/^\/admin\/workspaces\/task-management\/templates\/new$/, 'タスクテンプレート作成'],
    [/^\/admin\/workspaces\/task-management\/events$/, 'イベント一覧'],
    [/^\/admin\/workspaces\/task-management\/quick-tasks$/, 'クイックタスク'],
    [/^\/admin\/workspaces\/task-management\/projects\/[^/]+$/, 'タスク詳細'],
    [/^\/admin\/workspaces\/artist-management\/artists\/register$/, 'アーティスト登録'],
    [/^\/admin\/workspaces\/artist-management\/artists\/registration-complete$/, 'アーティスト登録完了'],
    [/^\/admin\/workspaces\/artist-management\/artists\/[^/]+$/, 'アーティスト詳細'],
    [/^\/admin\/workspaces\/artist-management\/artists$/, 'アーティスト一覧'],
    [/^\/admin\/workspaces\/artist-management\/groups$/, 'グループ設定'],
    [/^\/admin\/workspaces\/artist-management\/parts$/, 'パート編集'],
    [/^\/admin\/workspaces\/artist-management\/group-tags$/, 'グループタグ編集'],
    [/^\/admin\/workspaces\/communications\/artist-chat\/broadcast$/, '一斉送信'],
    [/^\/admin\/workspaces\/communications\/artist-chat$/, 'アーティストチャット'],
    [/^\/admin\/workspaces\/communications\/group-chat$/, 'グループチャット'],
    [/^\/admin\/workspaces\/casting\/offers\/new$/, 'オファー申請'],
    [/^\/admin\/workspaces\/casting\/offers\/by-hold\/[^/]+$/, 'オファー詳細'],
    [/^\/admin\/workspaces\/casting\/offers\/[^/]+$/, 'オファー詳細'],
    [/^\/admin\/workspaces\/casting\/offers$/, 'オファー履歴'],
    [/^\/admin\/workspaces\/contracts\/form(?:\/[^/]+)?$/, '本契約申請'],
    [/^\/admin\/workspaces\/contracts\/confirm$/, '本契約内容の確認'],
    [/^\/admin\/workspaces\/contracts\/success$/, '本契約送信完了'],
    [/^\/admin\/workspaces\/contracts\/history$/, '本契約履歴'],
    [/^\/admin\/workspaces\/contracts\/[^/]+\/schedule-edit$/, 'スケジュール変更'],
    [/^\/admin\/workspaces\/contracts\/[^/]+\/edit\/confirm\/personal$/, '契約内容の変更'],
    [/^\/admin\/workspaces\/contracts\/[^/]+\/edit\/confirm\/common$/, '契約内容の変更'],
    [/^\/admin\/workspaces\/contracts\/[^/]+\/edit$/, '本契約修正'],
    [/^\/admin\/workspaces\/contracts\/[^/]+$/, '本契約詳細'],
    [/^\/admin\/workspaces\/finance\/dashboard$/, '経理・集計'],
    [/^\/admin\/workspaces\/finance\/payments\/monthly$/, '請求一覧'],
    [/^\/admin\/workspaces\/finance\/payments\/invoices\/[^/]+$/, '請求書プレビュー'],
    [/^\/admin\/workspaces\/library\/music$/, '楽曲管理'],
    [/^\/admin\/workspaces\/concierge\/dashboard$/, 'コンシェルジュ'],
    [/^\/admin\/workspaces\/service-ai$/, 'Casticart AI'],
    [/^\/admin\/workspaces\/planning\/ai-planner$/, 'Casticart AI'],
    [/^\/admin\/workspaces\/event-operations\/events\/[^/]+\/surveys$/, 'アンケート'],
    [/^\/admin\/workspaces\/system\/service-ai-settings$/, 'Casticart AI設定'],
    [/^\/admin\/workspaces\/system\/service-ai-settings\/conversations$/, 'AI会話レビュー'],
    [/^\/admin\/workspaces\/system\/service-ai-settings\/usage-logs$/, 'AIトークン利用ログ'],
    [/^\/admin\/workspaces\/system\/announcements$/, 'お知らせ管理'],
    [/^\/admin\/workspaces\/system\/operational-emails$/, 'メール配信'],
    [/^\/admin\/workspaces\/system\/bugs$/, 'バグ管理'],
    [/^\/admin\/workspaces\/system\/account-management$/, 'アカウント管理'],
    [/^\/admin\/workspaces\/system\/policies$/, '規約管理'],
    [/^\/admin\/workspaces\/system\/mobile-release$/, 'モバイル更新管理'],
    [/^\/admin\/workspaces\/system\/mac-release$/, 'macOS更新管理'],
    [/^\/admin\/workspaces\/system\/action-logs$/, 'アクションログ'],
    [/^\/admin\/workspaces\/system\/manuals(?:\/.*)?$/, 'マニュアル'],
    [/^\/admin\/settings\/profile$/, 'プロフィール設定'],
    [/^\/admin\/settings\/change-password$/, 'パスワード変更'],
    [/^\/admin\/settings\/notifications$/, '通知設定'],
    [/^\/artist\/workspaces\/event-operations\/dashboard$/, 'ダッシュボード'],
    [/^\/artist\/workspaces\/event-operations\/events\/[^/]+\/timetables\/[^/]+$/, 'タイムテーブル表示'],
    [/^\/artist\/workspaces\/event-operations\/events\/[^/]+\/stage-seating(?:\/preview)?$/, 'シーティング'],
    [/^\/artist\/workspaces\/event-operations\/events\/[^/]+$/, 'イベント詳細'],
    [/^\/artist\/workspaces\/event-operations\/events$/, 'イベント一覧'],
    [/^\/artist\/workspaces\/event-operations\/event-chat$/, 'イベントチャット'],
    [/^\/artist\/workspaces\/event-operations\/venues\/[^/]+$/, '会場詳細'],
    [/^\/artist\/workspaces\/communications\/chat$/, 'チャット'],
    [/^\/artist\/workspaces\/casting\/offers\/[^/]+$/, 'オファー詳細'],
    [/^\/artist\/workspaces\/casting\/offers$/, 'オファー一覧'],
    [/^\/artist\/workspaces\/contracts\/dashboard$/, '本契約一覧'],
    [/^\/artist\/workspaces\/contracts\/[^/]+\/print$/, '依頼書プレビュー'],
    [/^\/artist\/workspaces\/contracts\/[^/]+$/, '本契約詳細'],
    [/^\/artist\/workspaces\/concierge\/apply$/, 'コンシェルジュ申し込み'],
    [/^\/artist\/workspaces\/concierge\/additional-info\/[^/]+$/, 'イベント情報登録'],
    [/^\/artist\/workspaces\/concierge\/dashboard$/, 'コンシェルジュ'],
    [/^\/artist\/workspaces\/finance\/rewards$/, '経理・報酬'],
    [/^\/artist\/workspaces\/finance\/invoice-maker$/, '請求書メーカー'],
    [/^\/artist\/workspaces\/support\/manuals(?:\/.*)?$/, 'マニュアル'],
    [/^\/artist\/workspaces\/support\/bug-reports$/, 'バグ報告'],
    [/^\/artist\/settings\/profile$/, 'プロフィール'],
    [/^\/artist\/settings\/email-change$/, 'メールアドレス変更'],
    [/^\/artist\/settings\/bank-info$/, '請求先情報'],
    [/^\/artist\/settings\/notifications$/, '通知設定'],
    [/^\/artist\/settings\/change-password$/, 'パスワード変更'],
    [/^\/artist\/settings\/artist-photography$/, '宣材写真'],
  ];
  const matched = rules.find(([pattern]) => pattern.test(normalized));
  if (matched) return matched[1];
  return '';
};
function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isTimetableView = location.pathname.includes('/timetables/');

  const headerRef = useHeaderHeightVar();

  useEffect(() => {
    if (!isTimetableView) return;
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty('--header-height', '0px');
  }, [isTimetableView]);

  // セッション（管理者 / アーティスト）
  const { user: adminUser } = useAuth();
  const { artistUser, isArtistDemo } = useArtistAuth();
  const { maintenanceMode } = useMaintenance();
  const loginPath = getLastLoginPath();

  // 未読・件数系（存在しない場合の安全なデフォルト）
  const {
    adminMessageCount = 0,
    adminGroupChatMessageCount = 0,
    eventChatMessageCount = 0,
    adminArtistChatTone = null,
    adminEventChatTone = null,
    adminGroupChatTone = null,
    conciergePendingCount = 0,
  } = useMessageCount?.() || {};

  // ドロップダウン開閉
  const [openMenu, setOpenMenu] = useState({ admin: false, artist: false });

  // 管理者・アーティストの軽量プロフィール（画像キーなど）
  const [adminProfile, setAdminProfile] = useState(null);
  const [artistProfile, setArtistProfile] = useState(null);

  // 即時非表示用フラグ（ログアウト成功イベントで強制的に消す）
  const [forceHide, setForceHide] = useState({ admin: false, artist: false });
  const [, setIsSidebarVisible] = useState(() => computeSidebarVisibility());

  const onAdminDash = location.pathname.startsWith('/admin/workspaces/event-operations/dashboard');
  const onArtistDash = location.pathname.startsWith('/artist/workspaces/event-operations/dashboard');
  const isAdminWorkspacePath =
    location.pathname.startsWith('/admin/workspaces') ||
    location.pathname.startsWith('/admin/settings');
  const isArtistWorkspacePath =
    location.pathname.startsWith('/artist/workspaces') ||
    location.pathname.startsWith('/artist/settings');

  const artistMenuRef = useRef(null);
  const adminMenuRef = useRef(null);
  useOutsideCloser(artistMenuRef, () => setOpenMenu((s) => (s.artist ? { ...s, artist: false } : s)));
  useOutsideCloser(adminMenuRef, () => setOpenMenu((s) => (s.admin ? { ...s, admin: false } : s)));

  // ---- プロフィール軽量取得（ログイン時のみ / 一発）
  const loadAdminProfile = useCallback(async () => {
    if (!adminUser || isArtistDemo) {
      setAdminProfile(null);
      return;
    }
    try {
      const profile = await getCurrentAdminProfile();
      setAdminProfile(profile || null);
    } catch {
      setAdminProfile(null);
    }
  }, [adminUser, isArtistDemo]);

  const loadArtistProfile = useCallback(async () => {
    if (!artistUser) {
      setArtistProfile(null);
      return;
    }
    try {
      const profile = await getCurrentArtistProfile();
      setArtistProfile(profile || null);
    } catch {
      setArtistProfile(null);
    }
  }, [artistUser]);

  useEffect(() => {
    // ログイン状態が変わった時のみフェッチ
    loadAdminProfile();
    loadArtistProfile();
  }, [loadAdminProfile, loadArtistProfile]);

  // 右上アイコン（管理者）
  const adminIcon = useMemo(() => {
    if (!adminUser) return null;
    const name =
      adminUser.full_name ||
      adminUser.nickname ||
      adminUser.username ||
      adminUser.name ||
      'Admin';
    // 画像キーはバックエンドの /api/admin/profile 優先
    const pictureKey =
      adminProfile?.profile_picture ||
      adminUser.profile_picture ||
      adminUser.profilePicture ||
      null;
    return { type: 'admin', name, pictureKey };
  }, [adminUser, adminProfile]);

  // 右上アイコン（アーティスト）
  const artistIcon = useMemo(() => {
    if (!artistUser) return null;
    const name =
      artistProfile?.name ||
      artistUser.name ||
      artistUser.full_name ||
      artistUser.displayName ||
      'Artist';
    const pictureKey =
      artistProfile?.profile_picture ||
      artistUser.profile_picture ||
      artistUser.profilePicture ||
      null;
    const gender = artistProfile?.gender || artistUser.gender || artistUser.artist_gender || '';
    return { type: 'artist', name, pictureKey, gender };
  }, [artistUser, artistProfile]);

  const announceSidebarVisibility = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('casticart:sidebar-visibility', {
        detail: { visible: computeSidebarVisibility() },
      })
    );
  }, []);

  const restoreSidebarPreference = useCallback(() => {
    if (typeof document === 'undefined') return;
    try {
      if (localStorage.getItem('layout.sidebarCollapsed') === 'true') {
        document.body.classList.add('sidebar-collapsed');
      } else {
        document.body.classList.remove('sidebar-collapsed');
      }
    } catch {}
    announceSidebarVisibility();
  }, [announceSidebarVisibility]);

  // 左：サイドバー開閉（最小処理 & 状態永続化）
  const toggleSidebar = useCallback(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (!body) return;
    const isMobile =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 700px)').matches;

    if (isMobile) {
      body.classList.toggle('sidebar-mobile-open');
      announceSidebarVisibility();
      return;
    }

    const collapsed = body.classList.toggle('sidebar-collapsed');
    try {
      localStorage.setItem('layout.sidebarCollapsed', String(collapsed));
    } catch {}
    window.dispatchEvent(new CustomEvent('casticart:toggle-sidebar', { detail: { collapsed } }));
    announceSidebarVisibility();
  }, [announceSidebarVisibility]);

  // 初期復元（1回だけ）
  useEffect(() => {
    restoreSidebarPreference();
  }, [restoreSidebarPreference]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('sidebar-mobile-open');
    announceSidebarVisibility();
  }, [announceSidebarVisibility, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 701px)');
    const handleChange = () => {
      if (mq.matches) {
        document.body.classList.remove('sidebar-mobile-open');
        announceSidebarVisibility();
      }
    };
    handleChange();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    }
    mq.addListener(handleChange);
    return () => mq.removeListener(handleChange);
  }, [announceSidebarVisibility]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 999px)');
    const handleChange = () => {
      if (!document?.body) return;
      if (mq.matches) {
        document.body.classList.add('sidebar-collapsed');
        announceSidebarVisibility();
      } else {
        restoreSidebarPreference();
      }
    };
    handleChange();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    }
    mq.addListener(handleChange);
    return () => mq.removeListener(handleChange);
  }, [announceSidebarVisibility, restoreSidebarPreference]);

  // タイトル → ダッシュボード（管理者優先）
  const goHome = useCallback(() => {
    // 完全停止モード中は常にサイトトップへ
    if (maintenanceMode === MAINTENANCE_MODES.FULL_STOP) {
      navigate('/');
      return;
    }

    const hasAdminSession = !!(adminUser && (adminUser.userId || adminUser.id));
    const hasArtistSession = !!(artistUser && (artistUser.userId || artistUser.id));
    const pathName = location.pathname || '';

    if (onAdminDash && hasAdminSession) {
      navigate(resolveAdminWorkspaceHomePath(pathName));
      return;
    }
    if (onArtistDash && hasArtistSession) {
      navigate(resolveArtistWorkspaceHomePath(pathName));
      return;
    }
    if (hasAdminSession && !hasArtistSession) {
      navigate(resolveAdminWorkspaceHomePath(pathName));
      return;
    }
    if (!hasAdminSession && hasArtistSession) {
      navigate(resolveArtistWorkspaceHomePath(pathName));
      return;
    }
    if (hasAdminSession && hasArtistSession) {
      if (pathName.startsWith('/admin/')) {
        navigate(resolveAdminWorkspaceHomePath(pathName));
        return;
      }
      if (pathName.startsWith('/artist/')) {
        navigate(resolveArtistWorkspaceHomePath(pathName));
        return;
      }
    }
    navigate('/');
  }, [adminUser, artistUser, location.pathname, maintenanceMode, navigate, onAdminDash, onArtistDash]);

  /* ─────────────────────────────────────────────
     ログアウト即時非表示イベント購読 & 復帰ハンドリング
     ───────────────────────────────────────────── */
  useEffect(() => {
    const onLoggedOut = (e) => {
      const role = e?.detail?.role;
      if (role === 'admin') {
        setForceHide((s) => ({ ...s, admin: true }));
        setAdminProfile(null);
        setOpenMenu((s) => ({ ...s, admin: false }));
      } else if (role === 'artist') {
        setForceHide((s) => ({ ...s, artist: true }));
        setArtistProfile(null);
        setOpenMenu((s) => ({ ...s, artist: false }));
      }
    };
    window.addEventListener('casticart:logout', onLoggedOut);
    return () => window.removeEventListener('casticart:logout', onLoggedOut);
  }, []);

  // ログインが戻ったら強制非表示を解除
  useEffect(() => {
    if (adminUser) setForceHide((s) => ({ ...s, admin: false }));
  }, [adminUser]);
  useEffect(() => {
    if (artistUser) setForceHide((s) => ({ ...s, artist: false }));
  }, [artistUser]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e) => {
      if (typeof e.detail?.visible === 'boolean') {
        setIsSidebarVisible(e.detail.visible);
      } else {
        setIsSidebarVisible(computeSidebarVisibility());
      }
    };
    window.addEventListener('casticart:sidebar-visibility', handler);
    setIsSidebarVisible(computeSidebarVisibility());
    return () => window.removeEventListener('casticart:sidebar-visibility', handler);
  }, []);

  // 表示可否（右上ユーザーアイコン）
  const isFullStop = maintenanceMode === MAINTENANCE_MODES.FULL_STOP;
  const showAdmin = !!adminUser && !forceHide.admin && !isFullStop && !isArtistDemo;
  const showArtist = !!artistUser && !forceHide.artist && !isFullStop;
  const isLoggedIn = showAdmin || showArtist || isArtistDemo;

  // —— ここからメニューボタンの表示条件 & バッジ計算 ——
  // 表示可否（メニューボタン）
  const showMenuButton =
    (isAdminWorkspacePath && !!adminUser && !forceHide.admin) ||
    (isArtistWorkspacePath && !!artistUser && !forceHide.artist);
  const adminPerms = Array.isArray(adminUser?.permissions) ? adminUser.permissions : [];
  const isAdminSuper =
    adminUser?.role === 'admin' ||
    (adminUser?.userType === 'admin' &&
      (adminUser?.permissions === 'admin' ||
        adminPerms.includes('admin') ||
        adminPerms.includes('*')));
  const hasConciergePermission =
    isAdminSuper || adminPerms.some((perm) => String(perm || '').toLowerCase().includes('concierge'));
  // マニュアル閲覧はサイドバーと同じく SYSTEM_VIEW_MANUALS（スーパー管理者は常に可）。
  const canViewManuals = isAdminSuper || adminPerms.includes('SYSTEM_VIEW_MANUALS');
  // Casticart AI を使える管理者にだけ自己利用リング/残り％を出す（NAV_AI_PLANNER 権限 or スーパー管理者）。
  const canUseCasticartAi =
    isAdminSuper || adminPerms.some((perm) => String(perm || '').toUpperCase().includes('NAV_AI_PLANNER'));
  const aiUsage = useHeaderAiUsage(Boolean(adminUser) && canUseCasticartAi && !isArtistDemo);

  const menuDotTone = useMemo(() => {
    if (!adminUser || isArtistDemo) return null;
    let hasAny = false;
    let allOk = true;
    if (Number(adminMessageCount || 0) > 0) {
      if (adminArtistChatTone == null) return null;
      hasAny = true;
      allOk = allOk && adminArtistChatTone === 'ok';
    }
    if (Number(eventChatMessageCount || 0) > 0) {
      if (adminEventChatTone == null) return null;
      hasAny = true;
      allOk = allOk && adminEventChatTone === 'ok';
    }
    if (Number(adminGroupChatMessageCount || 0) > 0) {
      if (adminGroupChatTone == null) return null;
      hasAny = true;
      allOk = allOk && adminGroupChatTone === 'ok';
    }
    if (Number(conciergePendingCount || 0) > 0) {
      hasAny = true;
      allOk = allOk && !hasConciergePermission;
    }
    if (!hasAny) return null;
    return allOk ? 'ok' : 'alert';
  }, [
    adminUser,
    adminMessageCount,
    adminArtistChatTone,
    eventChatMessageCount,
    adminEventChatTone,
    adminGroupChatMessageCount,
    adminGroupChatTone,
    conciergePendingCount,
    hasConciergePermission,
    isArtistDemo,
  ]);

  const showMenuUnreadDot = menuDotTone != null;
  const menuButtonLabel = showMenuButton
    ? `メニューの表示/非表示${showMenuUnreadDot ? '（未読あり）' : ''}`
    : 'メニューの表示/非表示';

  // ログアウト（最小フェッチ）
  const logoutAdmin = useCallback(async () => {
    try {
      const { ok, data } = await adminLogout();
      if (ok) {
        setLastLoginRole('admin');
        // 即時非表示
        window.dispatchEvent(new CustomEvent('casticart:logout', { detail: { role: 'admin' } }));
        setOpenMenu((s) => ({ ...s, admin: false }));
        navigate(data.redirect && data.redirect !== '/' ? data.redirect : '/admin-login');
      }
    } catch {}
  }, [navigate]);

  const logoutArtist = useCallback(async () => {
    try {
      const { ok, data } = await artistLogout();
      if (ok) {
        setLastLoginRole('artist');
        window.dispatchEvent(new CustomEvent('casticart:logout', { detail: { role: 'artist' } }));
        setOpenMenu((s) => ({ ...s, artist: false }));
        navigate(data.redirect && data.redirect !== '/' ? data.redirect : '/artist-login');
      }
    } catch {}
  }, [navigate]);

  const openAdminQuickTasks = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('casticart:quick-task-open'));
    }
    setOpenMenu((s) => ({ ...s, admin: false }));
  }, []);

  // トップページ（公開HP）はロゴを文字付き・左寄せで表示する
  const isHomePage = location.pathname === '/' || location.pathname === '';
  const headerClassName = `site-header${isTimetableView ? ' is-hidden' : ''}${
    isHomePage ? ' site-header--home' : ''
  }`;
  const menuButtonClassName = `menu-button header-menu-btn${showMenuUnreadDot ? ' has-unread' : ''}`;
  const loginButtonClassName = 'header-login-btn';
  const headerIconButtonClassName = 'header-icon-btn';
  const mobileHeaderPageTitle = useMemo(
    () => resolveMobileHeaderPageTitle(location.pathname),
    [location.pathname]
  );
  const adminDashboardMenuPath = useMemo(
    () => resolveAdminWorkspaceHomePath(location.pathname),
    [location.pathname]
  );
  const artistDashboardMenuPath = useMemo(
    () => resolveArtistWorkspaceHomePath(location.pathname),
    [location.pathname]
  );

  return (
    <header ref={headerRef} className={headerClassName}>
      <div className="site-header-container">
        {/* 左：メニュー（ハンバーガー） */}
        <div className="header-left">
          {showMenuButton && (
            <DsButton
              variant="ghost"
              icon
              className={menuButtonClassName}
              onClick={toggleSidebar}
              aria-label={menuButtonLabel}
              title="メニュー"
            >
              <i className="fa-solid fa-bars" aria-hidden="true"></i>
              {showMenuUnreadDot && (
                <span
                  className={`menu-button__dot${menuDotTone === 'ok' ? ' is-ok' : ''}`}
                  aria-hidden="true"
                ></span>
              )}
            </DsButton>
          )}
        </div>

        {/* 中央：タイトル（常に横幅のど真ん中） */}
        <div className="header-center">
          <button
            type="button"
            className="header-title"
            onClick={goHome}
            aria-label="ホームへ"
            title="Casticart|キャスティカート"
          >
            <img src={logoImg} alt="Casticart|キャスティカート|" className="header-logo header-logo--full" />
            <img src={logoIconImg} alt="" className="header-logo header-logo--icon" aria-hidden="true" />
            {mobileHeaderPageTitle ? (
              <span className="header-mobile-page-title">{mobileHeaderPageTitle}</span>
            ) : null}
          </button>
        </div>

        {/* 右：ログインアイコン（同時ログインなら両方表示） */}
        <div className="header-right">
          {/* 未ログイン時のCTA */}
          {!isLoggedIn && (
            <DsButton as={Link} to={loginPath} variant="ghost" className={loginButtonClassName} aria-label="ログイン">
              <i className="fa-solid fa-right-to-bracket" aria-hidden="true" />
              <span>ログイン</span>
            </DsButton>
          )}

          {/* アーティスト */}
          {showArtist && (
            <div className="header-user" ref={artistMenuRef}>
              <DsButton
                variant="ghost"
                icon
                className={headerIconButtonClassName}
                onClick={() =>
                  setOpenMenu((s) => ({ ...s, artist: !s.artist, admin: false }))
                }
                aria-haspopup="menu"
                aria-expanded={openMenu.artist ? 'true' : 'false'}
                title={artistIcon?.name || ''}
              >
                <HeaderAvatar
                  role="artist"
                  gender={artistIcon?.gender || ''}
                  name={artistIcon?.name || ''}
                  pictureKey={artistIcon?.pictureKey || ''}
                />
              </DsButton>

              {openMenu.artist && (
                <div className="user-menu ds-compact-action-menu" role="menu">
                  <Link
                    to={artistDashboardMenuPath}
                    className="user-menu__item"
                    onClick={() => setOpenMenu((s) => ({ ...s, artist: false }))}
                  >
                    <i className="fa-solid fa-grip" aria-hidden="true" />
                    <span>ダッシュボード</span>
                  </Link>
                  <Link
                    to="/artist/settings/profile"
                    className="user-menu__item"
                    onClick={() => setOpenMenu((s) => ({ ...s, artist: false }))}
                  >
                    <i className="fa-solid fa-user-gear" aria-hidden="true" />
                    <span>プロフィール</span>
                  </Link>
                  <Link
                    to="/artist/workspaces/support/manuals"
                    className="user-menu__item"
                    onClick={() => setOpenMenu((s) => ({ ...s, artist: false }))}
                  >
                    <i className="fa-solid fa-book" aria-hidden="true" />
                    <span>マニュアル</span>
                  </Link>
                  <button type="button" className="user-menu__item -danger" onClick={logoutArtist}>
                    <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
                    <span>ログアウト</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 管理者 */}
          {showAdmin && (
            <div className="header-user" ref={adminMenuRef}>
              <DsButton
                variant="ghost"
                icon
                className={headerIconButtonClassName}
                onClick={() =>
                  setOpenMenu((s) => ({ ...s, admin: !s.admin, artist: false }))
                }
                aria-haspopup="menu"
                aria-expanded={openMenu.admin ? 'true' : 'false'}
                title={adminIcon?.name || ''}
              >
                <HeaderAiAvatarRing meters={aiUsage.ready ? aiUsage.meters : null}>
                  <HeaderAvatar
                    role="admin"
                    name={adminIcon?.name || ''}
                    pictureKey={adminIcon?.pictureKey || ''}
                  />
                </HeaderAiAvatarRing>
              </DsButton>

              {openMenu.admin && (
                <div className="user-menu ds-compact-action-menu" role="menu">
                  <Link
                    to={adminDashboardMenuPath}
                    className="user-menu__item"
                    onClick={() => setOpenMenu((s) => ({ ...s, admin: false }))}
                  >
                    <i className="fa-solid fa-grip" aria-hidden="true" />
                    <span>ダッシュボード</span>
                  </Link>
                  <Link
                    to="/admin/settings/profile"
                    className="user-menu__item"
                    onClick={() => setOpenMenu((s) => ({ ...s, admin: false }))}
                  >
                    <i className="fa-solid fa-user-gear" aria-hidden="true" />
                    <span>プロフィール</span>
                  </Link>
                  <button type="button" className="user-menu__item" onClick={openAdminQuickTasks}>
                    <i className="fa-solid fa-list-check" aria-hidden="true" />
                    <span>クイックタスク</span>
                  </button>
                  {canViewManuals && (
                    <Link
                      to="/admin/workspaces/system/manuals"
                      className="user-menu__item"
                      onClick={() => setOpenMenu((s) => ({ ...s, admin: false }))}
                    >
                      <i className="fa-solid fa-book" aria-hidden="true" />
                      <span>マニュアル</span>
                    </Link>
                  )}
                  {aiUsage.ready ? <HeaderAiUsageMenuRows meters={aiUsage.meters} /> : null}
                  <button type="button" className="user-menu__item -danger" onClick={logoutAdmin}>
                    <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
                    <span>ログアウト</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
